/**
 * One app's page: the registry names which page an app opens, and this map
 * holds the component for each. A new kind of app is one entry here and one
 * in APP_DEFINITIONS (appRegistry).
 *
 * @module components/apps/AppDetailPage
 */
import { Navigate } from "@tanstack/react-router";
import type { ComponentType } from "react";

import { appDetailKind, type AppDetailKind } from "./appRegistry";
import { CiOverviewPage } from "./contractIntelligence/CiOverviewPage";
import { ProbePage } from "./probe/ProbePage";
import { IntegrationDetailPage } from "./integrations/IntegrationDetailPage";

export interface AppDetailProps {
  readonly organizationId: string | null;
  readonly orgSegment: string;
  readonly appId: string;
}

export const APP_DETAIL_COMPONENTS: Readonly<Record<AppDetailKind, ComponentType<AppDetailProps>>> =
  {
    "contract-intelligence": () => <CiOverviewPage />,
    probe: ({ orgSegment }) => <ProbePage orgSegment={orgSegment} />,
    // iManage is an area of its own (routes/$org.apps.imanage*); this only catches stale links.
    imanage: ({ orgSegment }) => (
      <Navigate to="/$org/apps/imanage" params={{ org: orgSegment }} replace />
    ),
    integration: ({ organizationId, orgSegment, appId }) => (
      <IntegrationDetailPage
        organizationId={organizationId}
        orgSegment={orgSegment}
        integrationId={appId}
      />
    ),
  };

export function AppDetailPage(props: AppDetailProps) {
  const Page = APP_DETAIL_COMPONENTS[appDetailKind(props.appId)];
  return <Page {...props} />;
}
